/**
 * ============================================================================
 * CourtsTable - COURTS LIST WITH FILTERING
 * ============================================================================
 *
 * Features:
 * - Sport filter dropdown
 * - Pricing display using utility function
 * - Recurring bookings badges
 * - Edit action
 * - Toggle switch for activation/deactivation
 *
 * Note: Delete and bulk actions removed for production.
 * Courts are created by super admin - vendors can only edit or deactivate.
 *
 * ============================================================================
 */

import React from 'react';
import { Edit, Grid, Loader2 } from 'lucide-react';
import { getCourtPricingDisplay, formatRecurringDiscounts } from '../../utils/courtFormatters';
import type { Court } from '../../types/courts';

interface CourtsTableProps {
  courts: Court[];
  sports: Array<{ id: number; name: string }>;
  filterSport: number | 'all';
  onFilterChange: (sportId: number | 'all') => void;
  onEdit: (court: Court) => void;
  onToggleActive: (court: Court, newActiveState: boolean) => void;
  togglingCourtId?: number | null;
  canManage?: boolean;
}

/**
 * Courts table with filtering
 */
export function CourtsTable({
  courts,
  sports,
  filterSport,
  onFilterChange,
  onEdit,
  onToggleActive,
  togglingCourtId,
  canManage = true,
}: CourtsTableProps) {
  // Filter courts by sport
  const filteredCourts =
    filterSport === 'all' ? courts : courts.filter((c) => c.sport_id === filterSport);

  if (courts.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <Grid className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-600 font-semibold">No courts or fields added yet</p>
        <p className="text-sm text-gray-500 mt-1">Click "Add Court/Field" to get started</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      {/* Table Header with Sport Filter */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Courts & Fields</h2>
            <p className="text-sm text-gray-600">{filteredCourts.length} courts</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Sport Filter */}
            <select
              value={filterSport}
              onChange={(e) =>
                onFilterChange(e.target.value === 'all' ? 'all' : Number(e.target.value))
              }
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-semibold bg-white text-gray-700 outline-none focus:border-blue-500"
            >
              <option value="all">All Sports</option>
              {sports.map((sport) => (
                <option key={sport.id} value={sport.id}>
                  {sport.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                Sport
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                Hourly Rate
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredCourts.map((court) => {
              const pricing = getCourtPricingDisplay(court);
              const discounts = formatRecurringDiscounts(court);

              return (
                <tr key={court.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-semibold text-gray-900">{court.name}</div>
                    {court.allow_recurring_bookings && (
                      <div className="mt-1 flex items-center text-xs text-purple-600">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-purple-100 font-semibold">
                          Recurring Available
                        </span>
                        {discounts && (
                          <span className="ml-2 text-green-600">({discounts})</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                      {court.sport?.name || 'Unknown'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-semibold text-gray-900">{pricing.label}</div>
                    <div className={`text-xs font-semibold ${pricing.className}`}>
                      {pricing.sublabel}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {/* Toggle Switch for Active/Inactive */}
                    <button
                      onClick={() => onToggleActive(court, !court.is_active)}
                      disabled={togglingCourtId === court.id || !canManage}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        court.is_active ? 'bg-green-500' : 'bg-gray-300'
                      } ${togglingCourtId === court.id ? 'opacity-50 cursor-wait' : ''} ${canManage ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
                      role="switch"
                      aria-checked={court.is_active}
                      title={!canManage ? "You don't have permission to manage courts" : court.is_active ? 'Click to deactivate' : 'Click to activate'}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          court.is_active ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      >
                        {togglingCourtId === court.id && (
                          <Loader2 className="h-4 w-4 animate-spin text-gray-400 m-0.5" />
                        )}
                      </span>
                    </button>
                    <span className={`ml-2 text-xs font-semibold ${court.is_active ? 'text-green-600' : 'text-gray-500'}`}>
                      {court.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => onEdit(court)}
                      disabled={!canManage}
                      className="text-blue-600 hover:text-blue-900 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-blue-600"
                      title={canManage ? 'Edit court' : "You don't have permission to manage courts"}
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden divide-y divide-gray-200">
        {filteredCourts.map((court) => {
          const pricing = getCourtPricingDisplay(court);
          const discounts = formatRecurringDiscounts(court);

          return (
            <div key={court.id} className="p-4 hover:bg-gray-50">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">{court.name}</p>
                  <p className="text-xs text-gray-500 mt-1">{court.sport?.name}</p>
                </div>
                {/* Mobile Toggle Switch */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onToggleActive(court, !court.is_active)}
                    disabled={togglingCourtId === court.id || !canManage}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      court.is_active ? 'bg-green-500' : 'bg-gray-300'
                    } ${togglingCourtId === court.id ? 'opacity-50 cursor-wait' : ''} ${canManage ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
                    role="switch"
                    aria-checked={court.is_active}
                    title={canManage ? undefined : "You don't have permission to manage courts"}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        court.is_active ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    >
                      {togglingCourtId === court.id && (
                        <Loader2 className="h-4 w-4 animate-spin text-gray-400 m-0.5" />
                      )}
                    </span>
                  </button>
                </div>
              </div>
              <div className="text-sm font-semibold text-gray-900 mb-1">{pricing.label}</div>
              <div className={`text-xs font-semibold ${pricing.className} mb-2`}>
                {pricing.sublabel}
              </div>
              {court.allow_recurring_bookings && discounts && (
                <p className="text-xs text-purple-600 mb-2">
                  Recurring: {discounts}
                </p>
              )}
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => onEdit(court)}
                  disabled={!canManage}
                  title={canManage ? undefined : "You don't have permission to manage courts"}
                  className="flex-1 px-3 py-2 text-sm font-semibold text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-blue-100"
                >
                  Edit
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
